/*
 * Byte-identical in Cortex and Quantum.
 *
 * THE CAMPAIGN JOURNEY'S SEMANTIC LAYER: WHAT A CAMPAIGN PROMOTES, WHO IT IS
 * FOR, AND WHICH CREATIVE THEME CARRIES WHICH STAGE.
 *
 * Dark Horse (campaign 13, 2026-10-12) is the incident. Its Campaign Journey
 * read "Introduce 2026 Theatrical Release to Political documentaries.", over a
 * funnel labelled POLITICAL DOCUMENTARIES. utils/strategyFunnel.js had
 *   - named a film campaign by its campaign name ahead of its advertiser. The
 *     campaign name is a flight label here; a film's advertiser IS the film.
 *     The governed title identity (backend cinemaCanonicalScopeService
 *     resolveCinemaTitleIdentity) is the title context, then the advertiser's
 *     name, and never the campaign name;
 *   - named its audience by the first saved planner entry, verbatim. That
 *     entry is an Interest Targeting value: a genre people are reached
 *     through, not a description of the people. The Audience Intelligence
 *     section beside the journey already reads every saved entry for what it
 *     is (utils/micrositeAudienceGroups.js); the journey did not; and
 *   - described its middle stage with the first creative theme it found: the
 *     hook's raw ad script ("… In theaters this summer. Get Tickets") on Dark
 *     Horse, and the template call-to-action theme on General pages.
 * Blood Behind Us read "Introduce Blood Behind Us 2027 Theatrical Release …"
 * the same way, and OutKick's journey ran a saved interest paragraph into the
 * sentence ("… to NFL football; college football; football analysis and…").
 *
 * This module names no campaign and invents nothing. Every name is a value
 * the public projection carries, and every audience is a saved planner entry
 * or a planning priority the backend serves, worded as what it is:
 *   people    an entry that names people ("cinema fans", "Hispanic adults
 *             25-54", "the faith community") is said as saved;
 *   interest  an entry that names what people are interested in (a genre, a
 *             topic, a sport, a title) is said as the audiences interested in
 *             it, which is what Interest Targeting means: "audiences
 *             interested in political documentaries";
 *   market    a served priority for an approved market is said as the
 *             audiences in it;
 *   layer     a proposal's audience layer keeps its own title.
 * Activation tactics are never the audience a campaign is introduced to:
 * Custom Targeting entries, planning priorities that only Custom Targeting
 * supplied, and retargeting, lookalike, first-party and sign-up pools wherever
 * they were saved. When the lead entry cannot be said as one of the four
 * (a head noun this layer does not know, a single person, a genre beside
 * people, an entry that may be an audience misread as an instruction), the
 * journey keeps its own neutral wording rather than guessing or naming a
 * lesser entry.
 *
 * The independent adversarial review of 2026-10-12 (review evidence
 * campaign-journey-semantics-20261012/adversarial) pinned the cases above;
 * its probes are the regression set in campaignJourneySemantics.test.js.
 */
import {
  classifyAudienceGroupEntry,
  cleanAudienceGroupText,
  readPlanningWeights,
} from "./micrositeAudienceGroups";

const object = (value) => (value && typeof value === "object" && !Array.isArray(value) ? value : {});
const list = (value) => (Array.isArray(value) ? value : []);
// Text the layer reads: strings and numbers only, whitespace normalized, at
// most 2,000 characters. No journey phrase is longer, and the cap keeps every
// rule below linear on whatever a payload carries.
const INPUT_LIMIT = 2000;
// Control and invisible format characters (bidi overrides, zero-width marks)
// are never part of a name or an audience; a saved value carrying them is
// read without them.
// eslint-disable-next-line no-control-regex -- matching control characters is the point
const INVISIBLE_CHARACTERS = /[\u0000-\u001F\u007F-\u009F\u200B-\u200F\u202A-\u202E\u2060-\u206F\uFEFF]/g;
const clean = (value) =>
  cleanAudienceGroupText(typeof value === "string" ? value.slice(0, INPUT_LIMIT * 2) : value)
    .replace(INVISIBLE_CHARACTERS, "")
    .replace(/\s{2,}/g, " ")
    .trim()
    .slice(0, INPUT_LIMIT);
const TERMINAL_CHARACTERS = new Set([" ", ".", ",", ":", ";", "!", "?", "…"]);
const trimTerminal = (value) => {
  const text = clean(value);
  let end = text.length;
  while (end > 0 && TERMINAL_CHARACTERS.has(text[end - 1])) end -= 1;
  return text.slice(0, end).trim();
};
const countWords = (value) => clean(value).split(" ").filter(Boolean).length;
// Two names are the same name in any case, punctuation or spacing.
const nameKey = (value) =>
  clean(value).toLowerCase().replace(/[’']/g, "").replace(/[^a-z0-9]+/g, " ").trim();
// A brand written in camel case keeps its own capitals ("iPhone owners",
// "eBay shoppers").
const capitalizeFirst = (value) => {
  const text = clean(value);
  if (/^[a-z][A-Z]/.test(text)) return text;
  return text ? `${text.charAt(0).toUpperCase()}${text.slice(1)}` : "";
};
const highest = (values) => values.reduce((top, value) => (value > top ? value : top), -Infinity);

export const JOURNEY_NAME_LIMIT = 64;
export const JOURNEY_LABEL_LIMIT = 72;
// The object of "Introduce <name> to …": with a name of at most 64 characters
// the sentence stays within the funnel's 200-character stage copy.
export const JOURNEY_PHRASE_LIMIT = 120;
const AUDIENCE_PHRASE_WORD_LIMIT = 14;

/* ---------------------------------------------------------------------------
 * Campaign identity: what the journey introduces.
 * ------------------------------------------------------------------------- */

// The film vertical in any of its served forms: the canonical keys "film" and
// "entertainment", and labels such as "Film / Entertainment".
const FILM_VERTICAL = /\b(?:film|cinema|movies?|theatrical|entertainment)\b/i;

// A connected source's suffix and a release qualifier are not part of a name:
// "Dark Horse (2026 American film) - Wikipedia" names Dark Horse.
const SOURCE_SUFFIX = /\s*[-–—|:]\s*(?:wikipedia|rotten\s+tomatoes|imdb|official\s+(?:website|site|trailer))\b.*$/i;
const RELEASE_QUALIFIER =
  /\s*\((?:\s*(?:19|20)\d{2}\s*|[^()]{0,40}\b(?:film|movie|documentary|series|miniseries)\b[^()]{0,20})\)\s*$/i;

// An advertiser named as a studio or distributor is not a film: its campaign
// name then carries the title it releases. A company suffix, or a distributor
// the planner names often; a film titled like one degrades to no name.
const COMPANY_NAME =
  /\b(?:pictures|studios?|films|filmworks|entertainment|releasing|distribution|distributors?|productions?|media|inc|llc|ltd|corp)\.?$/i;
const KNOWN_DISTRIBUTORS = new Set([
  "lionsgate", "lions gate", "a24", "neon", "fathom", "fathom events", "bleecker street", "focus features",
  "warner bros", "warner brothers", "universal", "paramount", "sony", "sony pictures classics", "disney",
  "walt disney", "searchlight", "pure flix", "kingdom story company", "roadside attractions", "magnolia",
  "ifc", "mubi", "oscilloscope", "gravitas ventures", "samuel goldwyn", "screen gems", "tristar", "miramax",
  "dreamworks", "pixar", "lucasfilm", "blumhouse", "stx", "stxfilms", "well go usa", "cinedigm",
  "kino lorber", "amazon mgm", "mgm", "netflix", "great american", "trafalgar",
]);
const isCompanyAdvertiser = (name) => COMPANY_NAME.test(name) || KNOWN_DISTRIBUTORS.has(nameKey(name));

// Words a flight label is made of: years, seasons, holidays, months, release
// and campaign vocabulary, channels and connectives. A campaign name of only
// these ("2026 Theatrical Release", "Christmas CTV 2026", "Fall 2026 Audience
// Growth") names a flight, not a title, product or brand.
const FLIGHT_LABEL_WORDS = new Set(
  `theatrical release rerelease re launch relaunch prelaunch pre campaign campaigns flight flights
  wave phase push burst sustain sustaining promo promotion promotional initiative program plan media
  paid digital social always on evergreen brand branding awareness consideration conversion conversions
  performance growth acquisition retargeting prospecting reach sales ticket tickets ticketing opening
  weekend premiere premieres debut rollout roll out tour test pilot activation support audience
  audiences fan fans engagement traffic lead leads signup signups national regional local us usa north
  america global international domestic nationwide market markets incremental expansion extension
  refresh update version part presale preorder home streaming svod avod tvod pvod vod dvd window
  spring summer fall autumn winter holiday holidays christmas xmas easter thanksgiving halloween
  valentines mothers fathers day black friday cyber monday back school new year years eve labor
  memorial season seasonal annual january february march april may june july august september
  october november december jan feb mar apr jun jul aug sep sept oct nov dec ctv ott olv tv video
  display search sem seo audio podcast podcasts radio dooh ooh programmatic native mobile desktop
  connected omnichannel multichannel cross channel platform integrated youtube meta facebook
  instagram tiktok x twitter snapchat pinterest reddit linkedin amazon google yahoo rumble spotify
  and the a an of for to in on with by at vs`.split(/\s+/).filter(Boolean)
);
// A campaign's own year is 2010–2039; "1917", "1984", "2000" and "2049" are
// titles. A bare number is no word of its own: it neither makes a name a
// flight label nor a title ("Release 2" is a flight label, "Toy Story 4" a
// title), and it is stripped only with a word that numbers flights ("Wave 2",
// "Campaign 53") or as a campaign year beside release words.
const FLIGHT_YEAR = /^20[1-3]\d$/;
const FLIGHT_LABEL_TOKEN = /^(?:20[1-3]\d|fy\d{2,4}|q[1-4]|h[12]|\d{1,4}(?:st|nd|rd|th|s)|v\d+|wk\d+|week\d*|phase\d*|wave\d*)$/;
const NUMBERED_FLIGHT_WORDS = new Set(
  "wave phase flight flights burst push promo campaign launch week sprint drop round season".split(" ")
);
const BARE_NUMBER = /^\d{1,4}$/;
// The release and campaign vocabulary that ends a studio's campaign name
// ("Jesus Revolution Theatrical Release"): never seasons, days or holidays,
// which titles carry ("Independence Day", "Halloween").
const RELEASE_WORDS = new Set(
  `theatrical release rerelease re launch relaunch prelaunch campaign campaigns flight flights wave phase
  push burst sustain promo promotion promotional initiative program awareness consideration conversion
  performance growth acquisition retargeting prospecting reach sales ticket tickets ticketing opening
  weekend premiere premieres debut rollout tour pilot activation paid digital social ctv ott olv video
  display search programmatic native mobile omnichannel multichannel integrated and the for of`.split(/\s+/).filter(Boolean)
);
const CAMPAIGN_VOCABULARY = /\b(?:theatrical|release|rerelease|campaigns?|flights?|launch|relaunch|push|wave|phase|burst|promo|promotion|initiative|ctv|ott|olv|programmatic|awareness|retargeting|prospecting|acquisition|growth|q[1-4]|fy\d{2,4}|20[1-3]\d)\b/i;

// The longest name the strategy page's served narratives say (backend
// utils/campaignJourneySemantics.js reads these rules with it): a film title
// can be longer than the journey's one sentence allows ("Dr. Strangelove or:
// How I Learned to Stop Worrying and Love the Bomb"); past this it is a
// paragraph, not a name.
export const NARRATIVE_NAME_LIMIT = 120;
// A link is never a name.
const URL_LIKE = /^(?:[a-z][a-z0-9+.-]*:\/\/|www\.)/i;

// Abbreviations a name may end with, full stop included ("Monsters, Inc.").
const NAME_ABBREVIATIONS = new Set(
  "inc co corp ltd llc jr sr bros st dr mr mrs ms vs vol no pt".split(" ")
);

// A name as saved, without a connected source's suffix, a release qualifier
// or a closing full stop (one that ends an abbreviation, an initialism or a
// single letter stays: "Monsters, Inc.", "S.W.A.T.", "W."); "" for what is
// not a name on any surface: a placeholder, a non-string, a link, a sentence
// (more than four words ending in a lower-case word and a full stop) or
// anything longer than the narratives' limit. Within that limit, its length
// is the caller's to judge.
const identityCandidate = (value) => {
  const name = clean(value).replace(SOURCE_SUFFIX, "").replace(RELEASE_QUALIFIER, "").trim();
  if (!name || URL_LIKE.test(name) || name.length > NARRATIVE_NAME_LIMIT) return "";
  if (/(?:^|\s)[a-z][a-z'’-]*\.$/.test(name) && countWords(name) > 4) return "";
  const lastWord = (name.split(" ").pop() || "").replace(/\.$/, "");
  const keepsFullStop = NAME_ABBREVIATIONS.has(lastWord.toLowerCase()) || lastWord.includes(".") || /^[A-Za-z]$/.test(lastWord);
  return /[^.]\.$/.test(name) && !keepsFullStop ? name.slice(0, -1).trim() : name;
};

const containsName = (value, name) => {
  const key = nameKey(name);
  return Boolean(key) && ` ${nameKey(value)} `.includes(` ${key} `);
};

/*
 * The words of a campaign name that are neither the advertiser's name nor
 * flight-label vocabulary: what the name says beyond "this advertiser's
 * campaign for this season, channel or release window".
 */
export const distinctiveCampaignNameWords = (campaignName, advertiserName = "") => {
  let key = ` ${nameKey(campaignName)} `;
  const own = nameKey(advertiserName);
  if (own) key = key.split(` ${own} `).join(" ");
  return key
    .split(" ")
    .filter(Boolean)
    .filter((token) => !FLIGHT_LABEL_WORDS.has(token) && !FLIGHT_LABEL_TOKEN.test(token) && !BARE_NUMBER.test(token));
};

export const isFlightLabelCampaignName = (campaignName, advertiserName = "") =>
  Boolean(nameKey(campaignName)) && distinctiveCampaignNameWords(campaignName, advertiserName).length === 0;

export const isCampaignFilmVertical = (campaign) => {
  const safe = object(campaign);
  return FILM_VERTICAL.test(clean(safe.advertiserVertical) || clean(safe.advertiser_vertical));
};

// A name without the release vocabulary and channels that end it, a flight's
// number with its word ("Wave 2") and a campaign year beside release words
// ("Blood Behind Us 2027 Theatrical Release" → "Blood Behind Us"). A number
// after a title word stays ("Toy Story 4", "Wonder Woman 1984 Theatrical
// Release" → "Wonder Woman 1984"), and so does a year at the very end.
const STRIPPABLE_FLIGHT_TOKEN = /^(?:fy\d{2,4}|q[1-4]|h[12]|v\d+|wk\d+|week\d*|phase\d+|wave\d+)$/;
// A release's scope goes only before the release words it qualifies ("Jesus
// Revolution 2026 National Release"); at the very end it may be a title's own
// word ("The Darjeeling Limited", "The International").
const RELEASE_SCOPE_WORDS = new Set("national nationwide wide limited domestic regional international global worldwide".split(" "));
const isReleaseWordKey = (key) => Boolean(key) && key.split(" ").every((part) => RELEASE_WORDS.has(part));
const withoutTrailingReleaseWords = (name) => {
  const words = clean(name).split(" ").filter(Boolean);
  let releaseWordsFollow = false;
  while (words.length) {
    const key = nameKey(words[words.length - 1]);
    if (!key || isReleaseWordKey(key) || STRIPPABLE_FLIGHT_TOKEN.test(key)) {
      words.pop();
      releaseWordsFollow = releaseWordsFollow || Boolean(key);
      continue;
    }
    const previous = nameKey(words[words.length - 2] || "");
    if (BARE_NUMBER.test(key) && NUMBERED_FLIGHT_WORDS.has(previous)) {
      words.splice(-2, 2);
      releaseWordsFollow = true;
      continue;
    }
    if (FLIGHT_YEAR.test(key) && (releaseWordsFollow || isReleaseWordKey(previous))) {
      words.pop();
      continue;
    }
    if (releaseWordsFollow && RELEASE_SCOPE_WORDS.has(key)) {
      words.pop();
      continue;
    }
    break;
  }
  return words.join(" ").replace(/[\s\-–—|:,]+$/u, "").trim();
};

/*
 * The film title a campaign name carries when its advertiser is a studio:
 * the name without the release words that end it ("Jesus Revolution
 * Theatrical Release" → "Jesus Revolution", "Riverlight Release" →
 * "Riverlight"). A name of only flight words is a title when it carries no
 * release or campaign vocabulary and no year ("Thanksgiving", "May December",
 * "Us"); "2026 Theatrical Release" is not.
 */
const campaignNameTitle = (campaignName, advertiser) => {
  if (!campaignName) return "";
  // A name that is one number is a title ("1917", "2012", "300"), and so is
  // one number before release words ("1917 Theatrical Re-Release"), unless
  // that number is the campaign's year.
  if (BARE_NUMBER.test(nameKey(campaignName))) return campaignName;
  const released = withoutTrailingReleaseWords(campaignName);
  if (released !== campaignName && BARE_NUMBER.test(nameKey(released)) && !FLIGHT_YEAR.test(nameKey(released))) return released;
  if (!isFlightLabelCampaignName(campaignName, advertiser)) {
    const title = withoutTrailingReleaseWords(campaignName);
    return distinctiveCampaignNameWords(title, advertiser).length ? title : "";
  }
  return CAMPAIGN_VOCABULARY.test(campaignName) ? "" : campaignName;
};

// A flight label that carries campaign vocabulary or a year ("2026
// Theatrical Release"); a title made of flight words alone is still a title
// ("Halloween", "The Premiere"), as campaignNameTitle reads it.
const isCampaignFlightLabel = (name) => isFlightLabelCampaignName(name) && CAMPAIGN_VOCABULARY.test(name);
// A campaign label saved where the film title belongs: flight words only,
// with campaign vocabulary twice or more ("2026 Theatrical Release",
// "Christmas CTV 2026") or a media channel ("Halloween CTV"). One other
// campaign word is a title ("1917", "Push", "Flight", "The Campaign").
const CAMPAIGN_VOCABULARY_ALL = new RegExp(CAMPAIGN_VOCABULARY.source, "gi");
const MEDIA_CHANNEL = /\b(?:ctv|ott|olv|dooh|programmatic)\b/i;
const isSavedCampaignLabel = (name) =>
  isFlightLabelCampaignName(name) && ((name.match(CAMPAIGN_VOCABULARY_ALL) || []).length >= 2 || MEDIA_CHANNEL.test(name));
// What a backend that reads these rules says the film-intelligence title is
// (routes/mediaCampaignRoutes.js serves the film's identity there).
const SERVED_IDENTITY_SOURCES = new Set(["film_title", "advertiser", "campaign"]);
// The film's name a backend that reads these rules serves in the
// film-intelligence section, or "" (an older backend, or no governed name):
// what a page section with no title of its own names the film by.
export const servedFilmName = (filmIntelligence) => {
  const section = object(filmIntelligence);
  return SERVED_IDENTITY_SOURCES.has(section.identitySource) ? identityCandidate(section.title) : "";
};

/*
 * A film title the campaign saved, as the journey reads it.
 *   - The saved film title is the governed name, taken as saved ("Toy Story
 *     4", "1917", "The Lost Weekend"): only what is no name at all
 *     (identityCandidate) or a campaign label saved in its place is refused.
 *   - A backend that reads these rules (backend utils/campaignJourneySemantics.js,
 *     generated from this file) serves the film's identity as the
 *     film-intelligence title and says where it came from (identitySource):
 *     the page names exactly what the served narratives name.
 *   - From an older backend, the film-intelligence title was the saved film
 *     title, else the campaign name, else the advertiser name: a title that
 *     is neither of the two names, and is not a stale artifact's campaign name
 *     (a release suffix, a campaign's flight label), is the saved one.
 */
const savedFilmTitle = (campaign, advertiser, campaignName) => {
  const explicit = identityCandidate(campaign.filmTitle);
  if (explicit && !isSavedCampaignLabel(explicit)) return explicit;
  const filmIntelligence = object(object(campaign.sections).filmIntelligence || campaign.filmIntelligence);
  const resolved = identityCandidate(filmIntelligence.title);
  const key = nameKey(resolved);
  if (!key || key === "this title") return "";
  if (SERVED_IDENTITY_SOURCES.has(filmIntelligence.identitySource)) return resolved;
  if (key === nameKey(campaignName) || key === nameKey(advertiser)) return "";
  const withoutRelease = withoutTrailingReleaseWords(resolved);
  if ((withoutRelease && withoutRelease !== resolved) || isCampaignFlightLabel(resolved)) return "";
  return resolved;
};

/*
 * The name the journey introduces, by source-of-truth precedence:
 *   film      the saved film title; the advertiser's name (on this platform a
 *             film's advertiser is the film) unless it is a studio or
 *             distributor; then the title the campaign name carries; else no
 *             name.
 *   other     the advertiser's name; the campaign name only when it extends
 *             the advertiser's own name with more than flight-label words (a
 *             product line); without an advertiser, a campaign name that is
 *             not only a flight label.
 * `source` says which: "film_title", "advertiser", "campaign" or "".
 *
 * `nameLimit` is the longest name the caller can say: the journey's sentence
 * by default, up to NARRATIVE_NAME_LIMIT for the served narratives. A saved
 * film title or an advertiser's name longer than that names nothing: never
 * the next source in its place, so the page and the narratives never name a
 * film two ways. A name the campaign name carries that is too long is passed
 * over, as before.
 */
export const resolveCampaignJourneyIdentity = (value, { nameLimit = JOURNEY_NAME_LIMIT } = {}) => {
  const campaign = object(value);
  const film = isCampaignFilmVertical(campaign);
  const limit = Number.isFinite(nameLimit) && nameLimit > 0 ? Math.min(nameLimit, NARRATIVE_NAME_LIMIT) : JOURNEY_NAME_LIMIT;
  const fit = (name) => (name.length <= limit ? name : "");
  const advertiser = identityCandidate(campaign.advertiserName);
  const campaignName = fit(identityCandidate(campaign.campaignName));
  const named = (name, source) => ({ name: fit(name), source, film });

  if (film) {
    const filmTitle = savedFilmTitle(campaign, advertiser, campaignName);
    if (filmTitle) return named(filmTitle, "film_title");
    if (advertiser && !isCompanyAdvertiser(advertiser)) return named(advertiser, "advertiser");
    const title = campaignNameTitle(campaignName, advertiser);
    return title ? named(title, "campaign") : named("", "");
  }
  if (advertiser) {
    const extendsAdvertiser =
      containsName(campaignName, advertiser) && !isFlightLabelCampaignName(campaignName, advertiser);
    return extendsAdvertiser ? named(campaignName, "campaign") : named(advertiser, "advertiser");
  }
  if (campaignName && !isFlightLabelCampaignName(campaignName)) return named(campaignName, "campaign");
  return named("", "");
};

/* ---------------------------------------------------------------------------
 * Audience: who the journey introduces the campaign to.
 * ------------------------------------------------------------------------- */

export const JOURNEY_AUDIENCE_KIND = Object.freeze({
  PEOPLE: "people",
  INTEREST: "interest",
  MARKET: "market",
  LAYER: "layer",
});

const words = (text) => text.split(/\s+/).filter(Boolean);
const wordSet = (text) => new Set(words(text));

// Plural nouns that name people. A phrase names people when its head (the
// last word before any qualifier) is one of these, or a plural agent noun
// ("farmers", "pastors", "cyclists", "musicians") that is not a thing.
const PEOPLE_PLURALS = wordSet(`
  people persons individuals adults men women males females girls boys guys gals ladies gentlemen babies
  toddlers infants preschoolers kids children teens teenagers tweens adolescents youths juveniles students
  graduates grads alumni undergrads millennials boomers zoomers seniors elders retirees parents moms mothers
  dads fathers grandparents grandmothers grandfathers grandmas grandpas grandchildren grandkids siblings
  sisters brothers daughters sons spouses husbands wives partners couples newlyweds singles widows widowers
  families households homeowners renters tenants landlords neighbors neighbours residents locals citizens
  immigrants expats travelers travellers tourists commuters drivers riders passengers pilots sailors soldiers
  troops marines veterans servicemembers officers cops firefighters responders nurses doctors physicians
  surgeons dentists therapists pharmacists caregivers patients survivors teachers educators professors
  coaches athletes players golfers runners cyclists swimmers skiers snowboarders surfers skaters climbers
  hikers campers hunters anglers fishermen outdoorsmen boaters bikers bowlers gamers streamers creators
  influencers bloggers podcasters artists musicians singers dancers actors filmmakers writers authors readers
  listeners viewers watchers audiences fans superfans followers subscribers members moviegoers filmgoers
  cinemagoers theatergoers theatregoers churchgoers concertgoers goers cinephiles attendees visitors users
  customers clients consumers shoppers buyers purchasers owners holders collectors hobbyists enthusiasts
  lovers buffs aficionados devotees connoisseurs foodies believers christians catholics evangelicals
  protestants baptists methodists pentecostals mormons jews muslims hindus buddhists worshippers worshipers
  congregants parishioners pastors priests ministers clergy rabbis imams missionaries seekers skeptics
  atheists voters conservatives liberals progressives moderates independents republicans democrats
  libertarians patriots activists advocates supporters donors volunteers americans africans asians
  europeans hispanics latinos latinas latinx mexicans canadians texans professionals workers employees
  employers executives managers leaders founders entrepreneurs developers engineers designers marketers
  investors traders borrowers applicants homebuyers farmers ranchers growers builders contractors
  tradespeople electricians plumbers mechanics truckers lawyers attorneys accountants bankers brokers agents
  realtors snowbirds nesters cutters makers homeschoolers learners beginners newcomers communities those
  critics diabetics agnostics alcoholics epileptics asthmatics fanatics
`);
// A noun for one group of people, said with "the": "the faith community".
const PEOPLE_COLLECTIVES = wordSet("audience community fanbase congregation diaspora");
// Nouns that name a thing (a genre, a format, a topic, a product), never
// people, including the plural agent-looking ones ("thrillers", "trailers").
const TOPIC_NOUNS = wordSet(`
  documentaries documentary films film movies movie dramas drama comedies comedy thrillers thriller westerns
  western musicals musical biopics biopic mysteries mystery romances romance horror horrors stories story
  shows show podcasts podcast books book novels novel memoirs biographies histories games game videos video
  cartoons cartoon sitcoms sitcom concerts concert programs programming channels channel series sports sport
  news esports athletics events event festivals festival tournaments tournament leagues league debates debate
  discussions politics issues causes values rights freedoms liberties traditions holidays recipes products
  brands gadgets devices cars trucks suvs vehicles boats guns firearms crafts arts hobbies pets dogs cats
  horses animals plants gardens homes renovations renovation remodels remodeling projects deals sales coupons
  offers investments stocks finances loans mortgages vacations trips destinations hotels cruises flights
  restaurants foods drinks beers wines spirits snacks beverages supplements vitamins workouts exercises
  classes courses lessons careers jobs studies sciences technologies apps platforms networks websites blogs
  magazines newspapers publications articles reviews ratings trailers teasers posters previews highlights
  interviews analyses commentaries broadcasts streams playlists albums songs genres titles franchises sequels
  superheroes comics manga anime blockbusters sneakers shoes watches burgers stickers computers printers
  controllers chargers papers letters numbers centers covers layers banners filters containers flowers
  rivers towers matters mirrors colors motors tractors sensors flavors errors rumors monsters lists
  checklists wishlists studios outdoors parks fights matches activities breweries wineries photos tacos
  order theater theatre soccer summer winter water weather paper letter number center career power border
  cancer dinner beer liquor color humor terror honor labor flavor decor motor error floor door outdoor
  interior
`);
// Words ending in "s" that are singular.
const SINGULAR_S = wordSet(`
  bias texas christmas atlas canvas gas alias chaos ethos cosmos kudos news series species means lens jesus
  tennis analysis basis crisis campus status bonus focus chorus virus census thesis
`);
const PERSON_IRREGULAR = wordSet("man woman child person");
const GENERATION = /\b(?:gen(?:eration)?[\s-]*(?:x|y|z|alpha)(?:ers)?|baby\s+boomers|millennials|zoomers)$/i;
// What follows a phrase's head: a relative clause, a preposition, a
// demographic qualifier, a note.
const HEAD_QUALIFIER =
  /\s+(?:who|whom|whose|that|which|where|when|with|without|within|in|of|for|from|on|at|across|near|around|outside|inside|interested|aged?|ages|between|over|under|including|like|such|hhi|income)\b|\s*[(:;—–]/i;
const AGE_TAIL =
  /\s+(?:aged?\s+)?\d{1,2}\s*(?:[-–—]|to)\s*\d{1,2}\+?$|\s+\d{1,2}\s*\+$|\s+\d{1,2}\s+(?:and|or)\s+(?:over|older|up|under|younger)$/i;
const CONJUNCTION = /\s*,\s*(?:(?:and|or)\s+)?|\s+(?:and|or|&)\s+|\s*\/\s*/i;
const DETERMINER = /^(?:the|a|an|our|their|its|this|these|those)\s/i;

/*
 * A planner entry that is an activation tactic rather than an audience:
 * retargeting and lookalike pools, site, page and video-completion pools,
 * first-party, CRM and subscriber lists, sign-ups, clickers and geofences.
 * Saved under Custom Targeting or under Interests, it is never the audience a
 * campaign is introduced to.
 */
const TACTIC_AUDIENCE = new RegExp(
  [
    "\\b(?:retarget\\w*|re-target\\w*|remarket\\w*|look-?alikes?|seed\\s+audiences?)\\b",
    "\\b(?:site|website|web|page|landing[\\s-]page|ticket[\\s-]page|app)\\s+visitors\\b",
    "\\b(?:trailer|video)\\s+(?:viewers|watchers|engagers|completers)\\b",
    "\\b(?:viewers|completers|watchers)\\s+over\\s+\\d+\\s*%",
    "\\bengagers\\b|\\b(?:engagement|retargeting|audience|warm)\\s+pools?\\b",
    "\\b(?:email|e-mail|newsletter|sms|text)\\s+(?:subscribers|lists?|sign-?ups)\\b",
    "\\bcrm\\b|\\bcustomer\\s+(?:lists?|match|files?)\\b|\\b(?:first|1st)[\\s-]party\\b",
    "\\b(?:existing|current|past|lapsed)\\s+(?:customers|purchasers|buyers|subscribers)\\b",
    "\\bsign[\\s-]?ups?\\b|\\bclickers\\b|\\b(?:cart\\s+)?abandoners\\b|\\bgeo[\\s-]?fenc\\w*",
  ].join("|"),
  "i"
);
export const isTacticAudienceEntry = (value) => TACTIC_AUDIENCE.test(clean(value));

const headParts = (phrase) => {
  const core = phrase.split(HEAD_QUALIFIER)[0].trim() || phrase;
  return core.split(CONJUNCTION).map((part) => part.replace(AGE_TAIL, "").trim()).filter(Boolean);
};

// Leading words that are proper (a faith, people, party, language or place),
// so a capital on them is never only the start of the planner's sentence.
const PROPER_LEADS = wordSet(`
  christian christians catholic catholics evangelical evangelicals protestant protestants baptist baptists
  jewish jews muslim muslims mormon mormons lds hindu hindus buddhist buddhists hispanic hispanics latino
  latinos latina latinas latinx black asian asians african africans american americans native indigenous
  european europeans spanish english french german italian chinese japanese korean indian arab mexican
  mexicans canadian canadians british irish texan texans southern midwestern republican republicans
  democrat democrats democratic bible biblical god
`);
// Descriptive and genre words that are never proper, so a capital on them can
// only be the start of the planner's sentence.
const COMMON_LEADS = wordSet(`
  political politics historical history biographical independent indie documentary faith faith-based
  religious spiritual family home college high local regional national international sports sport outdoor
  outdoors active avid frequent recent casual young older new first-time horror comedy drama action crime
  true science sci-fi fantasy animated animation classic classical contemporary modern vintage retro
  conservative liberal progressive luxury budget premium organic healthy health fitness wellness travel
  music live streaming online mobile social digital financial personal real broad general film movie cinema
  theatrical theater theatre book books reading cooking food gardening parenting pet pets dog cat beauty
  fashion tech technology gaming video news current world military patriotic patriot rural urban suburban
  small youth kids children teen senior female male football basketball baseball soccer golf tennis hockey
  racing motorsport boxing wrestling fishing hunting camping hiking running cycling yoga crafts art arts
  design interior kitchen auto automotive car cars truck trucks motorcycle diy education learning career
  business investing shopping retail holiday seasonal gift gifts wedding dating romance romantic mystery
  thriller western superhero anime country gospel rock pop jazz comic comics long-form short-form
  alternative mainstream free law government empty decision first cord teen hometown home-team neighborhood
`);

/*
 * What a part's head noun names: "people", "collective" (one group, said
 * with "the"), "person" (one person: unsayable), "interest" (a thing or a
 * topic), "agent" (a plural agent noun this layer does not list: people,
 * unless the phrase is a name), or "unknown" (a plural this layer does not
 * know: unsayable, since it may name people as easily as things).
 */
const headKind = (part) => {
  if (GENERATION.test(part)) return "people";
  const head = (words(part).pop() || "").toLowerCase().replace(/[’']s$/, "").replace(/[^a-z-]/g, "");
  if (!head) return "interest";
  if (PEOPLE_PLURALS.has(head)) return "people";
  if (PEOPLE_COLLECTIVES.has(head)) return "collective";
  if (TOPIC_NOUNS.has(head)) return "interest";
  if (PERSON_IRREGULAR.has(head) || PEOPLE_PLURALS.has(`${head}s`) || PEOPLE_PLURALS.has(`${head}es`) ||
    (head.endsWith("y") && PEOPLE_PLURALS.has(`${head.slice(0, -1)}ies`))) return "person";
  if (head.length > 4 && /(?:ers|ors|ists|ians)$/.test(head)) return "agent";
  const plural = head.length > 3 && head.endsWith("s") && !/(?:ss|us|is|ics)$/.test(head) && !SINGULAR_S.has(head);
  return plural ? "unknown" : "interest";
};

/*
 * Whether a saved phrase names people, names what people are interested in,
 * or cannot be said as either ("mixed"). The head is read before any
 * qualifier ("Fans of No Country for Old Men and Hell or High Water" names
 * fans; "Adults 25-54 HHI $100K+" names adults). Coordinated heads share a
 * noun ("Crime and action thriller fans", "Hispanic, Black, and Asian
 * American adults"): they name people when the last part does and every
 * earlier part is people itself or a single qualifying word; things likewise.
 * A single person, a plural this layer does not know, and a genre beside
 * people ("Political documentaries and documentary viewers") are "mixed".
 */
export const classifyJourneyAudiencePhrase = (value) => {
  const phrase = trimTerminal(value);
  if (!phrase) return "";
  const parts = headParts(phrase);
  if (!parts.length) return "";
  const kinds = parts.map(headKind);
  const last = kinds[kinds.length - 1];
  // A Title Case name whose head is only guessed ("Florida Gators", "Texas
  // Longhorns") names a team, title or brand: what people are interested in.
  if (last === "agent" || last === "unknown") {
    const core = parts.join(" ");
    const lead = (words(core)[0] || "").toLowerCase();
    const titleCase = words(core).length > 1 && words(core).every((word) => /^[A-Z0-9]/.test(word));
    if (titleCase && !COMMON_LEADS.has(lead) && !PROPER_LEADS.has(lead)) return JOURNEY_AUDIENCE_KIND.INTEREST;
  }
  // Earlier parts agree with the head, or are one word that can qualify it:
  // any known word before people ("Hispanic, Black, and … adults"), a thing
  // before a thing ("law and order"), never people before a thing.
  const earlier = (accepted, single) =>
    parts.slice(0, -1).every((part, index) =>
      accepted.includes(kinds[index]) || (countWords(part) === 1 && single.includes(kinds[index])));
  if (last === "people" || last === "collective" || last === "agent") {
    return earlier(["people", "collective", "agent"], ["people", "collective", "agent", "person", "interest"])
      ? JOURNEY_AUDIENCE_KIND.PEOPLE
      : "mixed";
  }
  if (last === "interest") return earlier(["interest"], ["interest"]) ? JOURNEY_AUDIENCE_KIND.INTEREST : "mixed";
  return "mixed";
};

// The words a campaign's own saved entries write in lower case.
const lowerCaseWords = (entries) => {
  const found = new Set();
  for (const entry of list(entries).slice(0, 200)) {
    for (const word of clean(entry).split(/[^A-Za-z'’-]+/)) if (/^[a-z][a-z'’-]*$/.test(word)) found.add(word);
  }
  return found;
};

/*
 * A phrase as it reads inside a sentence. The planner's lists are saved split
 * from one sentence, so the first entry carries that sentence's capital
 * ("Political documentaries, faith-based films, …"), and the served planning
 * model capitalizes each concept's label for display ("Golf fans"). That
 * capital is dropped only where it can only be that: from a common word (a
 * descriptive or genre word, a word the campaign's own entries write in
 * lower case, or a common noun for people) that starts an otherwise
 * lower-case phrase, and from a people phrase's leading article ("The faith
 * community"). A proper word ("Christian films", "Texas football"), a numbered
 * name ("Big 12 football"), a single word that may be a name ("Netflix") and a
 * Title Case or acronym-led phrase ("College Football", "NFL football") keep
 * their case.
 */
const inSentence = (phrase, commonWords, kind) => {
  const text = trimTerminal(phrase);
  const [first = "", second = "", ...rest] = words(text);
  const lower = first.toLowerCase();
  if (!/^[A-Z][a-z][a-z'’-]*$/.test(first) || PROPER_LEADS.has(lower)) return text;
  const lowerCased = `${first.charAt(0).toLowerCase()}${text.slice(1)}`;
  const people = kind === JOURNEY_AUDIENCE_KIND.PEOPLE;
  const peopleNoun = people && (PEOPLE_PLURALS.has(lower) || PEOPLE_COLLECTIVES.has(lower));
  if (people && DETERMINER.test(text)) return lowerCased;
  // A capitalized word after the first makes the phrase Title Case (a name);
  // an acronym ("HHI", "NFL") or a number does not.
  if ([second, ...rest].some((word) => /^[A-Z][a-z]/.test(word))) return text;
  if (peopleNoun) return lowerCased;
  if (!second || /^\d/.test(second)) return text;
  return COMMON_LEADS.has(lower) || commonWords.has(lower) ? lowerCased : text;
};

// A people phrase in a sentence: a group noun takes "the" ("church members
// and the faith community"), unless an article is already there.
const sayPeople = (phrase) => {
  const core = phrase.split(HEAD_QUALIFIER)[0].trim() || phrase;
  const qualifier = phrase.slice(core.length);
  const separators = core.match(new RegExp(CONJUNCTION.source, "gi")) || [];
  const parts = core.split(CONJUNCTION);
  const said = parts.map((part) => {
    const kind = headKind(part.replace(AGE_TAIL, "").trim());
    return kind === "collective" && !DETERMINER.test(part) ? `the ${part}` : part;
  });
  return said.reduce((text, part, index) => `${text}${index ? separators[index - 1] : ""}${part}`, "") + qualifier;
};

// "Interest in Christian films" names Christian films.
const withoutInterestLead = (phrase) => phrase.replace(/^(?:an?\s+)?(?:interests?|interested)\s+in\s+/i, "");

// Plural content nouns and the form they take before "audiences".
const CONTENT_NOUN_SINGULAR = Object.freeze({
  documentaries: "documentary", films: "film", movies: "movie", dramas: "drama", comedies: "comedy",
  thrillers: "thriller", westerns: "western film", musicals: "musical", biopics: "biopic", mysteries: "mystery",
  romances: "romance", stories: "story", shows: "show", podcasts: "podcast", books: "book", novels: "novel",
  games: "game", videos: "video", cartoons: "cartoon", sitcoms: "sitcom", concerts: "concert",
  programs: "program", channels: "channel", series: "series", sports: "sports", news: "news",
  esports: "esports", athletics: "athletics",
});

// "Political documentaries" as a label for the people: "Political documentary
// audiences". Only a short single concept whose head is a known content noun
// or a singular; anything else keeps the full "Audiences interested in …".
const interestAudienceLabel = (topic) => {
  const text = trimTerminal(topic);
  if (!text || countWords(text) > 4 || /[,;]|\s(?:and|or|&)\s/i.test(text)) return "";
  const parts = words(text);
  const last = parts[parts.length - 1];
  const singular = CONTENT_NOUN_SINGULAR[last.toLowerCase()];
  if (singular) {
    // Keep the saved word's case: "DOCUMENTARIES", "Documentaries", "documentaries".
    parts[parts.length - 1] =
      last === last.toUpperCase() ? singular.toUpperCase()
        : /^[A-Z]/.test(last) ? capitalizeFirst(singular) : singular;
  } else if (/[^s]s$/i.test(last)) {
    return "";
  }
  return `${parts.join(" ")} audiences`;
};

const joinPair = (phrases) => phrases.filter(Boolean).slice(0, 2).join(" and ");
const fits = (label) => (label && label.length <= JOURNEY_LABEL_LIMIT ? label : "");

const NO_AUDIENCE = Object.freeze({ kind: "", phrase: "", label: "", source: "" });

/*
 * One or two phrases of the same kind as the journey says them: `phrase` is
 * the object of "Introduce <name> to …", `label` the funnel's audience label.
 * Both are built from the same words, so they always agree; a phrase or label
 * that does not fit names no audience. `evidence` is the campaign's own saved
 * entries, read for the words it writes in lower case.
 */
export const describeJourneyAudience = (kind, phrases, { evidence = [], source = "" } = {}) => {
  // An entry saved twice is said once; a number alone ("42") names no one.
  const seen = new Set();
  const items = list(phrases).map(trimTerminal).filter((item) => /\p{L}/u.test(item))
    .filter((item) => {
      const key = item.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, 2)
    .map((item) => (kind === JOURNEY_AUDIENCE_KIND.INTEREST ? withoutInterestLead(item) : item));
  if (!items.length || items.some((item) => !item)) return NO_AUDIENCE;
  if (items.some((item) => countWords(item) > AUDIENCE_PHRASE_WORD_LIMIT || item.length > JOURNEY_NAME_LIMIT)) {
    return NO_AUDIENCE;
  }
  const commonWords = lowerCaseWords(evidence);
  // A market is a place name: always said as served.
  const said = kind === JOURNEY_AUDIENCE_KIND.MARKET ? items : items.map((item) => inSentence(item, commonWords, kind));
  let phrase = "";
  let label = "";
  if (kind === JOURNEY_AUDIENCE_KIND.PEOPLE) {
    phrase = joinPair(said.map(sayPeople));
    label = fits(capitalizeFirst(joinPair(said)));
  } else if (kind === JOURNEY_AUDIENCE_KIND.INTEREST) {
    phrase = `audiences interested in ${joinPair(said)}`;
    label = fits((said.length === 1 && capitalizeFirst(interestAudienceLabel(said[0]))) || capitalizeFirst(phrase));
  } else if (kind === JOURNEY_AUDIENCE_KIND.MARKET) {
    phrase = `audiences in ${joinPair(said)}`;
    label = fits(capitalizeFirst(phrase));
  }
  if (!phrase || !label || phrase.length > JOURNEY_PHRASE_LIMIT) return NO_AUDIENCE;
  return { kind, phrase, label, source };
};

// One or two phrases said together when they are the same kind and fit; else
// the first alone.
const describePhrases = (phrases, options) => {
  const items = list(phrases).map(trimTerminal).filter(Boolean).slice(0, 2);
  const kinds = items.map(classifyJourneyAudiencePhrase);
  const kind = kinds[0];
  if (!items.length || (kind !== JOURNEY_AUDIENCE_KIND.PEOPLE && kind !== JOURNEY_AUDIENCE_KIND.INTEREST)) return NO_AUDIENCE;
  // Never "A and B and C": a phrase that already joins words is said alone.
  const compound = (item) => /\s(?:and|or|&)\s|[,;]/i.test(item);
  if (items.length === 2 && kinds[1] === kind && !items.some(compound)) {
    const pair = describeJourneyAudience(kind, items, options);
    if (pair.kind) return pair;
  }
  return describeJourneyAudience(kind, [items[0]], options);
};

const withoutEntry = ({ kind, phrase, label, source }) => ({ kind, phrase, label, source });

/*
 * The served planning priorities under presentation contract v2, read through
 * readPlanningWeights (the page never names a row the Audience section would
 * refuse), with each row's tier, fold, market and planner fields from the
 * served model.
 */
const servedPlanningPriorities = (section) => {
  const model = readPlanningWeights(section);
  if (!model) return null;
  if (model.withheld) return { rows: [], withheld: true };
  const served = list(object(section.planningWeights).rows);
  return {
    withheld: false,
    rows: model.rows.map((row, index) => {
      const source = object(served[index]);
      const priority = Number(source.planningPriority);
      const fields = new Set(list(source.sourceSignals).map((signal) => clean(object(signal).field)).filter(Boolean));
      return {
        ...row,
        aggregate: source.aggregate === true,
        market: source.market && typeof source.market === "object" ? source.market : null,
        fields,
        priority: Number.isFinite(priority) ? priority : row.weight,
      };
    }),
  };
};

const AUDIENCE_PRIORITY_CATEGORIES = ["core_affinity", "local_market_affinity"];
// A priority the journey may name: an audience or market priority that a
// planner field other than Custom Targeting supplied, and not a tactic.
const nameablePriority = (row) =>
  AUDIENCE_PRIORITY_CATEGORIES.includes(row.category) &&
  !(row.fields.size && [...row.fields].every((field) => field === "customTargeting")) &&
  !isTacticAudienceEntry(row.displayLabel);

/*
 * The audience the served model names first (the backend's served copy reads
 * this same rule: routes/mediaCampaignRoutes.js resolveMicrositeLeadAudience),
 * over the priorities the journey may name: the top-scored one, two tied named together; more than
 * two, or a folded list on top, name no one audience. A priority with an
 * approved market is said as that market; a local affinity without one
 * ("Local restaurants") is said as what it names. Two tied priorities of
 * different wording name the first only.
 */
const leadPlanningPriority = (rows, evidence) => {
  const audiences = rows.filter(nameablePriority);
  if (!audiences.length) return null;
  const top = highest(audiences.map((row) => row.priority));
  const lead = audiences.filter((row) => row.priority === top);
  if (lead.length > 2 || lead.some((row) => row.aggregate)) return null;
  const isMarket = (row) => row.category === "local_market_affinity" && Boolean(row.market);
  const sameWording = lead.filter((row) => isMarket(row) === isMarket(lead[0]));
  const labels = sameWording.map((row) => row.displayLabel);
  if (isMarket(lead[0])) {
    return describeJourneyAudience(JOURNEY_AUDIENCE_KIND.MARKET, labels, { source: "planning_priority" });
  }
  return describePhrases(labels, { evidence, source: "planning_priority" });
};

const SOURCE_INTEREST = "interest targeting";
const SOURCE_CUSTOM = "custom targeting";
const SOURCE_FALLBACK = "planner fallback";
const sourceKey = (cohort) => clean(cohort.source).toLowerCase();
// Entries the presenter positively reads as something other than an audience.
const NOT_AN_AUDIENCE = ["theme", "exclusion", "condition", "fragment"];

/*
 * The planner's lead audience among its saved entries: the first saved entry
 * that is an audience. Custom Targeting entries, the planner default, tactics
 * saved anywhere, and entries the presenter positively reads as a theme,
 * exclusion, condition or fragment of a note are passed over. An entry it
 * reads as an instruction or a note may be an audience whose first word is
 * also a verb ("Track and field fans", "Target shoppers"): the journey names
 * no audience then, never the entry after it. From a field the page cannot
 * name, an entry that itself names people is an audience. A saved list names
 * its first two items. When the lead cannot be said, no audience is named.
 */
const savedLeadAudience = (section, evidence) => {
  for (const cohort of list(section.cohorts).map(object)) {
    const label = clean(cohort.label);
    const key = sourceKey(cohort);
    if (!label || key === SOURCE_FALLBACK || key === SOURCE_CUSTOM || isTacticAudienceEntry(label)) continue;
    const { category, items } = classifyAudienceGroupEntry(label, cohort.source);
    if (NOT_AN_AUDIENCE.includes(category)) continue;
    if (category === "audience") {
      const phrases = (items.length >= 2 ? items : [label]).filter((item) => !isTacticAudienceEntry(item)).slice(0, 2);
      return { ...describePhrases(phrases, { evidence, source: "saved_entry" }), entry: phrases[0] || label };
    }
    const namesPeople = classifyJourneyAudiencePhrase(label) === JOURNEY_AUDIENCE_KIND.PEOPLE;
    if (category === "note" && key !== SOURCE_INTEREST && !items.length && namesPeople) {
      return { ...describeJourneyAudience(JOURNEY_AUDIENCE_KIND.PEOPLE, [label], { evidence, source: "saved_entry" }), entry: label };
    }
    return { ...NO_AUDIENCE, entry: label };
  }
  return null;
};

// Whether the served model weighs a saved entry as a priority it may name.
const modelWeighs = (rows, entry) => {
  const key = nameKey(entry);
  return Boolean(key) && rows.some((row) =>
    nameablePriority(row) && [row.displayLabel, ...list(row.members)].some((label) => nameKey(label) === key));
};

/*
 * The journey's audience, from the strongest source the projection carries:
 *   1. a proposal's first audience layer, by its own title (proposal mode);
 *   2. under presentation contract v2, the served model's lead priority; when
 *      the model names no one lead (a folded list on top), the planner's first
 *      saved audience only where the model weighs it as a priority the
 *      journey may name;
 *   3. otherwise the planner's first saved audience.
 * Returns { kind, phrase, label, source }; kind "" when no audience is named.
 */
export const resolveJourneyAudience = (value, { proposalPlanning = null } = {}) => {
  const campaign = object(value);
  if (proposalPlanning) {
    const layer = list(object(object(proposalPlanning).audience).layers).map(object)
      .find((entry) => clean(entry.title));
    return layer
      ? { kind: JOURNEY_AUDIENCE_KIND.LAYER, phrase: "", label: fits(clean(layer.title)), source: "proposal_layer" }
      : NO_AUDIENCE;
  }
  const section = object(object(campaign.sections).audienceIntelligence || campaign.audienceIntelligence);
  // The campaign's own saved entries: the evidence for which words it writes
  // in lower case (see inSentence).
  const targeting = object(campaign.targeting);
  const evidence = [
    ...list(section.cohorts).slice(0, 50).map((cohort) => object(cohort).label),
    ...list(targeting.interests).slice(0, 50),
    ...list(targeting.customTargeting).slice(0, 50),
  ];
  const model = servedPlanningPriorities(section);
  if (model) {
    if (model.withheld) return NO_AUDIENCE;
    const modelEvidence = [...evidence, ...model.rows.slice(0, 50).flatMap((row) => [row.displayLabel, ...list(row.members).slice(0, 50)])];
    const lead = leadPlanningPriority(model.rows, modelEvidence);
    if (lead) return lead.kind ? lead : NO_AUDIENCE;
    const saved = savedLeadAudience(section, modelEvidence);
    return saved && saved.kind && modelWeighs(model.rows, saved.entry) ? withoutEntry(saved) : NO_AUDIENCE;
  }
  const saved = savedLeadAudience(section, evidence);
  return saved && saved.kind ? withoutEntry(saved) : NO_AUDIENCE;
};

/* ---------------------------------------------------------------------------
 * Creative themes: which stage a theme carries, and whether its copy says a
 * purpose or is the ad itself.
 * ------------------------------------------------------------------------- */

export const CREATIVE_THEME_ROLE = Object.freeze({
  AWARENESS: "awareness",
  CONSIDERATION: "consideration",
  ACTION: "action",
});

// Call-to-action and conversion vocabulary only: "Action Spectacle" on an
// action film is a story theme, not the close.
const ACTION_THEME =
  /\b(?:action\s+trigger|call[\s-]to[\s-]action|take\s+action|cta|conversions?|converting|convert|urgency|purchases?|checkout|retarget\w*|re-?engag\w*|tickets?|booking|sign[\s-]?ups?|registration)\b/i;
const AWARENESS_THEME = /\b(?:hook|teaser|tease|awareness|introduc\w*|discovery|first[\s-]look|reveal|announce\w*)\b/i;

/*
 * The stage a creative theme serves, by its own title: a hook or teaser opens
 * the journey, a call to action or conversion push closes it, and every other
 * theme (proof, persuasion, story, familiarity) carries the middle stage.
 */
export const classifyCreativeThemeRole = (theme) => {
  const title = clean(object(theme).title);
  if (ACTION_THEME.test(title)) return CREATIVE_THEME_ROLE.ACTION;
  if (AWARENESS_THEME.test(title)) return CREATIVE_THEME_ROLE.AWARENESS;
  return CREATIVE_THEME_ROLE.CONSIDERATION;
};

const CTA_VERB =
  "(?:get|buy|book|watch|see|shop|order|pre-?order|reserve|stream|download|subscribe|sign\\s+up|join|learn|find|discover|explore|register|donate|call|visit|try|start|claim|apply|enroll|play|listen|rent|request)";
// A button: a call-to-action verb that starts the copy's last fragment, a
// few words, any closing punctuation ("… only in theaters. Watch the
// Trailer!").
const CTA_FINAL = new RegExp(
  `(?:^|[.!?\\u2026\\u2014\\u2013]\\s*)(${CTA_VERB}\\b(?:\\s+[^\\s.!?\\u2026]+){0,4})\\s*[.!?\\u2026]*$`,
  "i"
);
// A release or availability line ("In theaters July 4", "Only in theaters").
const AVAILABILITY =
  /\b(?:(?:only\s+)?in\s+theat(?:er|re)s|in\s+cinemas|now\s+playing|now\s+streaming|streaming\s+now|coming\s+soon|available\s+now|on\s+sale\s+now|tickets\s+on\s+sale|out\s+now)\b/i;

/*
 * Copy that is the ad itself rather than a description of the strategy:
 * copy that ends on a button ("… In theaters now. Get Tickets", "Watch the
 * Trailer!"), or a release line among short headline fragments ("One Man.
 * One Nation. … Only In Theaters"). Prose that mentions a call to action
 * mid-sentence ("… point viewers to Watch the Trailer") is a description.
 * The journey describes stages; the Creative Messaging section shows the ads.
 */
export const isCreativeExecutionCopy = (value) => {
  const copy = clean(value);
  if (!copy) return false;
  const button = copy.match(CTA_FINAL);
  if (button) {
    const parts = words(button[1]);
    if (parts.length >= 2 && parts.filter((word) => /^[A-Z]/.test(word)).length >= 2) return true;
  }
  if (!AVAILABILITY.test(copy)) return false;
  const fragments = copy.split(/[.!?…—–]+/).map((fragment) => fragment.trim()).filter(Boolean);
  return fragments.filter((fragment) => countWords(fragment) <= 4).length >= 2;
};

// The backend's template creative summary ("Creative strategy is built to
// keep <source title> visually cinematic while adapting message pressure
// across <platforms>."): it names the campaign by a connected source's title
// and lists channels, so it describes no stage.
export const isTemplateCreativeSummary = (value) => /^creative strategy is built to keep\b/i.test(clean(value));

export const capitalizeJourneyText = capitalizeFirst;
